class CartDrawer extends HTMLElement {
  constructor() {
    super();
    // Mirrors the merchant's free-shipping threshold (in cents) as rendered into
    // data-threshold by snippets/cart-drawer.liquid. All progress/threshold math is
    // done Liquid-side and stays there; this field exists purely so JS consumers
    // (this class, other scripts, the console) can read the same value the current
    // markup was rendered with. 0 when the setting is absent.
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.isUnlockedState = false;
    this.confettiAnimId = null;
    this.recommendationsCache = {};
    this.productCache = {};
    this.isDetailView = false;
  }

  connectedCallback() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.canvas = document.getElementById('cart-confetti-canvas');

    this.bindEvents();
    this.checkInitialState();
  }

  t(key, fallback = '') {
    return (window.cart_drawer && window.cart_drawer.translations && window.cart_drawer.translations[key]) || fallback;
  }

  /**
   * USD-only by design. The store's money_format is `${{amount}}` and there is no
   * multi-currency setup, so the symbol is hardcoded rather than pulling in a
   * currency-formatting dependency.
   *
   * Numbers only: every caller passes a value straight out of Shopify's JS cart /
   * product APIs, which express money as an integer number of cents. The previous
   * string branch stripped the first '.' from a string, which happened to convert
   * "12.50" into 1250 but turned anything else (a formatted "$12.50", "1,250") into
   * NaN, so it is gone rather than extended.
   */
  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '$0.00';
    return '$' + (amount / 100).toFixed(2);
  }

  /**
   * Merchandise units only — the shipping-insurance line is a real cart line and
   * must never show up in the header count. Falls back to the raw Shopify count
   * when assets/shipping-insurance.js is absent or insurance is switched off.
   */
  merchandiseCount(cart) {
    const filters = window.CartDrawerFilters;
    if (filters && typeof filters.merchandiseCount === 'function') {
      return filters.merchandiseCount(cart);
    }
    return cart ? cart.item_count : 0;
  }

  /**
   * Announces every cart render on the document. `source` lets a listener that
   * caused the mutation recognise and skip its own event instead of reacting to it
   * and looping.
   */
  dispatchCartUpdated(cart, source) {
    document.dispatchEvent(
      new CustomEvent('cart-drawer:updated', {
        bubbles: true,
        detail: { cart: cart, source: source || 'cart-drawer' },
      })
    );
  }

  /**
   * External cart mutation (shipping insurance). Re-renders from the section
   * endpoint, then re-announces with the caller's source so the caller can ignore
   * its own event instead of looping.
   */
  async renderFromExternal(cart, source) {
    this.currentCart = cart;
    if (!this.isDetailView) {
      await this.renderCartState(cart);
    }
    this.updateHeaderBadge(this.merchandiseCount(cart));
    this.dispatchCartUpdated(cart, source);
  }

  // --- Confetti Particle System (Top to Bottom, Scoped to .cart-drawer__card) ---
  triggerFallingConfetti() {
    // Merchant setting. Only an explicit `false` disables it, so a missing flag
    // (older markup, section not yet re-rendered) keeps today's behaviour.
    if (window.cart_drawer && window.cart_drawer.confetti_enabled === false) return;

    const canvas = this.canvas || document.getElementById('cart-confetti-canvas');
    if (!canvas) return;

    const cardEl = this.querySelector('.cart-drawer__card') || canvas.parentElement;
    const width = cardEl ? cardEl.clientWidth : 380;
    const height = cardEl ? cardEl.clientHeight : window.innerHeight;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    const particleCount = 100;
    const particles = [];
    const colors = [
      '#610322', '#57efc5', '#ffd700', '#ff69b4', 
      '#00ffff', '#9932cc', '#ff7f50', '#70e000', '#ff4d6d'
    ];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: -15 - Math.random() * 120, // Start above top of the card
        width: 6 + Math.random() * 7,
        height: 10 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 2.2,
        vy: 2.8 + Math.random() * 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        oscillationSpeed: 0.03 + Math.random() * 0.05,
        oscillationAmplitude: 1.2 + Math.random() * 1.8
      });
    }

    if (this.confettiAnimId) {
      cancelAnimationFrame(this.confettiAnimId);
    }

    let frameCount = 0;
    const maxFrames = 240; // ~4s duration at 60fps

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;
      let activeParticles = 0;

      particles.forEach(p => {
        if (p.y < canvas.height + 30) {
          activeParticles++;
          p.y += p.vy;
          p.x += p.vx + Math.sin(frameCount * p.oscillationSpeed) * p.oscillationAmplitude;
          p.rotation += p.rotationSpeed;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
          ctx.restore();
        }
      });

      if (activeParticles > 0 && frameCount < maxFrames) {
        this.confettiAnimId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animate();
  }

  bindEvents() {
    // Intercept clicks on cart triggers and links targeting /cart across the site
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('a[href*="/cart"]') || 
                       e.target.closest('#cartButtonTrigger') || 
                       e.target.closest('#CartButton') || 
                       e.target.closest('[data-cart-drawer-toggle]');
      
      if (cartLink) {
        const href = cartLink.href || cartLink.getAttribute('href') || '';
        // Ignore checkout links or specific external links
        if (!href.includes('/checkout')) {
          e.preventDefault();
          this.openCart();
          return;
        }
      }

      // Close cart button/overlay click
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        this.closeCart();
        return;
      }

      // Back button click in detail view
      if (e.target.closest('[data-cart-back]')) {
        e.preventDefault();
        this.hideProductDetails();
        return;
      }

      // Show Details button click
      const showDetailsBtn = e.target.closest('[data-fbt-show-details]');
      if (showDetailsBtn) {
        e.preventDefault();
        const handle = showDetailsBtn.dataset.handle;
        if (handle) {
          this.showProductDetails(handle);
        }
        return;
      }

      // FBT Add to Cart button click
      const fbtAtcBtn = e.target.closest('[data-fbt-atc]');
      if (fbtAtcBtn) {
        e.preventDefault();
        const handle = fbtAtcBtn.dataset.handle;
        const variantId = fbtAtcBtn.dataset.variantId;
        const hasVariants = fbtAtcBtn.dataset.hasVariants === 'true';
        
        if (handle && hasVariants) {
          this.openVariantModal(handle);
        } else if (variantId) {
          this.addVariant(variantId, fbtAtcBtn);
        } else if (handle) {
          this.openVariantModal(handle);
        }
        return;
      }

      // Variant modal close button
      if (e.target.closest('[data-variant-modal-close]')) {
        e.preventDefault();
        this.closeVariantModal();
        return;
      }

      // Variant confirm button inside modal
      const confirmBtn = e.target.closest('#cart-drawer-variant-confirm');
      if (confirmBtn) {
        e.preventDefault();
        const select = this.querySelector('#cart-drawer-variant-select');
        if (select && select.value) {
          this.addVariant(select.value, confirmBtn);
          this.closeVariantModal();
        }
        return;
      }

      // Quantity controls (+ / -)
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        e.preventDefault();
        const lineKey = qtyBtn.dataset.lineKey;
        const currentQty = parseInt(qtyBtn.dataset.currentQty, 10);
        const change = parseInt(qtyBtn.dataset.qtyChange, 10);
        const newQty = Math.max(0, currentQty + change);
        this.updateLineItem(lineKey, newQty);
        return;
      }

      // Remove item button
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        const lineKey = removeBtn.dataset.lineKey;
        this.updateLineItem(lineKey, 0);
        return;
      }
    });

    // Intercept all product form submissions targeting /cart/add.
    //
    // Capture phase + stopPropagation, deliberately. The legacy
    // StyleHatch.AjaxCart in assets/theme.js binds submit directly on each
    // [data-AddToCartForm] > form, and a handler bound to the target element runs
    // before a bubble-phase listener on document. It called preventDefault() but
    // not stopPropagation(), so BOTH handlers fired: the item was POSTed twice and
    // the legacy flyout cart appeared immediately while this drawer waited on its
    // own round-trips. Capturing on document puts us first and stops the event
    // before it ever reaches the form, so this drawer is the sole owner of
    // add-to-cart.
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        e.stopPropagation();
        const formData = new FormData(form);
        this.submitAddToCartForm(formData);
      }
    }, true);

    // Handle window resize for confetti canvas
    window.addEventListener('resize', () => {
      const canvas = this.canvas || document.getElementById('cart-confetti-canvas');
      if (canvas) {
        const cardEl = this.querySelector('.cart-drawer__card') || canvas.parentElement;
        canvas.width = cardEl ? cardEl.clientWidth : 380;
        canvas.height = cardEl ? cardEl.clientHeight : window.innerHeight;
      }
    });
  }

  checkInitialState() {
    const msgEl = this.querySelector('#free-shipping-msg');
    if (msgEl) {
      this.isUnlockedState = msgEl.dataset.unlocked === 'true';
    }
  }

  showLoading() {
    const overlay = this.querySelector('.cart-drawer__loading-overlay');
    if (overlay) {
      overlay.classList.add('is-loading');
    }
  }

  hideLoading() {
    const overlay = this.querySelector('.cart-drawer__loading-overlay');
    if (overlay) {
      overlay.classList.remove('is-loading');
    }
  }

  openCart() {
    this.classList.add('is-open');
    if (this.panel) this.panel.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('cart-drawer-open');

    if (this.isUnlockedState) {
      this.triggerFallingConfetti();
    }
  }

  closeCart() {
    this.classList.remove('is-open');
    if (this.panel) this.panel.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('cart-drawer-open');
    this.hideProductDetails();
    this.closeVariantModal();
  }

  async clearDiscountAndAttributes() {
    try {
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      
      // 1. Post JSON to /cart/update.js with empty discount and null attribute
      await fetch(rootPath + 'cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: '',
          attributes: {
            'discount': null
          }
        })
      });

      // 2. Post FormData to /cart/update.js as secondary fallback
      const formData = new FormData();
      formData.append('discount', '');
      formData.append('attributes[discount]', '');
      await fetch(rootPath + 'cart/update.js', {
        method: 'POST',
        body: formData
      });

      // 3. Clear discount session & cookie fallbacks.
      //
      // These are /discount/<code> URLs, so each one is a storefront navigation
      // that returns a full HTML page — awaiting them one after another was three
      // page downloads in series and the single biggest cost in this method.
      // They are best-effort session cleanup and none of them depends on the
      // others, so they go out together.
      await Promise.all([
        fetch('/discount/CLEAR', { credentials: 'same-origin' }),
        fetch('/discount/NONE', { credentials: 'same-origin' }),
        fetch('/discount/0', { credentials: 'same-origin' })
      ]);
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      const freshRes = await fetch(rootPath + 'cart.js');
      if (freshRes.ok) {
        return await freshRes.json();
      }
    } catch (discErr) {
      console.warn('Error clearing discount and attributes:', discErr);
    }
    return null;
  }

  async checkAndClearDiscountIfNoShopTheLook(cart) {
    if (!cart || !cart.items) return cart;

    const hasShopTheLookItem = cart.items.some(item => {
      if (!item.properties) return false;
      if (Array.isArray(item.properties)) {
        return item.properties.some(p => p[0] === '_shop_the_look' || p[0] === '_discount_code');
      }
      if (typeof item.properties === 'object') {
        return item.properties._shop_the_look || item.properties._discount_code;
      }
      return false;
    });

    // Discount CODES only. clearDiscountAndAttributes() can only remove a code —
    // an automatic discount reapplies itself server-side no matter what we send.
    // The old predicate also matched cart_level_discount_applications and
    // line_level_total_discount, which are exactly what an automatic discount
    // looks like, so a store running one (e.g. "15% off qualifying line items")
    // paid the full six-request clear on every single quantity change and never
    // cleared anything.
    const hasAppliedDiscount = (cart.attributes && cart.attributes.discount && cart.attributes.discount !== '') ||
                               (cart.discount_codes && cart.discount_codes.length > 0);

    // If no Shop The Look items remain but a discount / attribute is present, manually clear it
    if (!hasShopTheLookItem && hasAppliedDiscount) {
      const updatedCart = await this.clearDiscountAndAttributes();
      if (updatedCart) return updatedCart;
    }

    return cart;
  }

  async refreshCart() {
    this.showLoading();
    try {
      const res = await fetch('/cart.js');
      let cart = await res.json();
      cart = await this.checkAndClearDiscountIfNoShopTheLook(cart);
      // Assigned after the discount clean-up so currentCart matches what is
      // rendered and dispatched below rather than the pre-clear cart.
      this.currentCart = cart;

      if (!this.isDetailView) {
        await this.renderCartState(cart);
      }
      this.updateHeaderBadge(this.merchandiseCount(cart));
      this.dispatchCartUpdated(cart, 'cart-drawer');
    } catch (err) {
      console.error('Error refreshing cart:', err);
    } finally {
      this.hideLoading();
    }
  }

  async updateLineItem(lineKey, quantity) {
    this.showLoading();
    try {
      // Check if target item being updated/removed is a Shop The Look item
      const currentItem = this.currentCart && this.currentCart.items 
        ? this.currentCart.items.find(i => i.key === lineKey || i.id === lineKey || String(i.variant_id) === String(lineKey))
        : null;

      const isShopTheLookTarget = currentItem && currentItem.properties && (
        currentItem.properties._shop_the_look || currentItem.properties._discount_code
      );

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      let cart = await res.json();

      // If a Shop The Look item is modified or removed, clear the discount code &
      // attributes.
      //
      // `|| quantity === 0` used to be here, which forced the six-request clear on
      // EVERY removal — including in a cart with no discount whatsoever. That made
      // deleting a line cost eight sequential round-trips. Removing a line is not
      // by itself a reason to clear a discount; the else-branch below already asks
      // the precise question against the post-change cart and costs nothing when
      // the answer is no.
      if (isShopTheLookTarget) {
        const clearedCart = await this.clearDiscountAndAttributes();
        if (clearedCart) cart = clearedCart;
      } else {
        cart = await this.checkAndClearDiscountIfNoShopTheLook(cart);
      }

      this.currentCart = cart;
      if (!this.isDetailView) {
        await this.renderCartState(cart);
      }
      this.updateHeaderBadge(this.merchandiseCount(cart));
      this.dispatchCartUpdated(cart, 'cart-drawer');
    } catch (err) {
      console.error('Error updating cart line item:', err);
    } finally {
      this.hideLoading();
    }
  }

  async addVariant(variantId, buttonEl) {
    if (buttonEl) {
      buttonEl.disabled = true;
      const span = buttonEl.querySelector('span');
      if (span) span.innerText = this.t('adding', 'Adding...');
    }

    // Same ordering as submitAddToCartForm: on screen first, network after.
    this.isDetailView = false;
    this.openCart();
    this.showLoading();
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const added = await res.json();
      if (added && added.status && Number(added.status) >= 400) {
        throw new Error(added.description || added.message || 'Add to cart failed');
      }
      await this.refreshCart();
    } catch (err) {
      console.error('Error adding variant to cart:', err);
    } finally {
      this.hideLoading();
      if (buttonEl) {
        buttonEl.disabled = false;
        const span = buttonEl.querySelector('span');
        if (span) span.innerText = this.t('add_to_cart', 'Add To Cart');
      }
    }
  }

  async submitAddToCartForm(formData) {
    // Open before any network work. This used to await the add, then /cart.js,
    // then the section render, and only then open — three sequential round-trips
    // during which nothing happened on screen. The drawer now slides in on the
    // click with its spinner up, and fills in as the responses land.
    this.isDetailView = false;
    this.openCart();
    this.showLoading();
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      const added = await res.json();
      // /cart/add.js answers 4xx with a JSON body rather than rejecting, so a
      // sold-out or unavailable variant has to be caught here or refreshCart()
      // would quietly re-render the unchanged cart as if it had worked.
      if (added && added.status && Number(added.status) >= 400) {
        throw new Error(added.description || added.message || 'Add to cart failed');
      }
      await this.refreshCart();
    } catch (err) {
      console.error('Error submitting add to cart form:', err);
    } finally {
      this.hideLoading();
    }
  }

  updateHeaderBadge(count) {
    const countEl = document.getElementById('CartCount');
    if (countEl) {
      countEl.textContent = count;
    }
  }

  async fetchProductData(handle) {
    if (this.productCache[handle]) {
      return this.productCache[handle];
    }
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      this.productCache[handle] = data;
      return data;
    } catch (err) {
      console.error('Error fetching product details:', err);
      return null;
    }
  }

  // --- Show Product Details inside Cart Drawer ---
  async showProductDetails(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      this.isDetailView = true;

      // Update Header: Show Back button
      const backBtn = this.querySelector('[data-cart-back]');
      if (backBtn) {
        backBtn.classList.remove('is-hidden');
      }

      const bodyEl = this.querySelector('.cart-drawer__body');
      if (bodyEl) {
        const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
        const priceFormatted = this.formatMoney(product.price);
        const descriptionHtml = product.description || '';
        const hasVariants = product.variants && product.variants.length > 1;
        const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';

        bodyEl.innerHTML = `
          <div class="cart-drawer__detail-view">
            <img src="${imageSrc}" alt="${product.title}" class="cart-drawer__detail-img">
            <h3 class="cart-drawer__detail-title">${product.title}</h3>
            <button class="cart-drawer__detail-atc" data-fbt-atc data-handle="${product.handle}" data-variant-id="${firstVariantId}" data-has-variants="${hasVariants}">
              <span>${this.t('add_to_cart', 'Add To Cart')}</span>
            </button>
            <div class="cart-drawer__detail-desc">
              ${descriptionHtml}
            </div>
          </div>
        `;
      }
    } catch (err) {
      console.error('Error showing product details:', err);
    } finally {
      this.hideLoading();
    }
  }

  async hideProductDetails() {
    this.isDetailView = false;
    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn) {
      backBtn.classList.add('is-hidden');
    }

    await this.refreshCart();
  }

  // --- Variant Selector Modal ---
  async openVariantModal(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      // Exclude sold out variants
      const availableVariants = (product.variants || []).filter(v => v.available);
      if (availableVariants.length === 0) {
        alert(this.t('sold_out', 'Sold Out'));
        return;
      }

      // If only 1 available variant, add to cart directly
      if (availableVariants.length === 1) {
        this.addVariant(availableVariants[0].id);
        return;
      }

      const modalEl = this.querySelector('#cart-drawer-variant-modal');
      const contentEl = this.querySelector('#cart-drawer-variant-modal-content');
      const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
      const priceFormatted = this.formatMoney(product.price);

      const optionLabel = product.options && product.options[0] ? product.options[0].name : 'Option';

      const optionsHtml = availableVariants.map(v => {
        return `<option value="${v.id}">${v.title} - ${this.formatMoney(v.price)}</option>`;
      }).join('');

      if (contentEl && modalEl) {
        contentEl.innerHTML = `
          <button class="cart-drawer__variant-modal-close" data-variant-modal-close aria-label="Close modal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="cart-drawer__variant-modal-row">
            <div class="cart-drawer__variant-modal-img" style="--item-img-url: url('${imageSrc}');"></div>
            <div class="cart-drawer__variant-modal-info">
              <span class="cart-drawer__variant-modal-title">${product.title}</span>
              <span class="cart-drawer__variant-modal-price">${priceFormatted}</span>
            </div>
          </div>
          <label class="cart-drawer__variant-label" for="cart-drawer-variant-select">${optionLabel}</label>
          <select class="cart-drawer__variant-select" id="cart-drawer-variant-select">
            ${optionsHtml}
          </select>
          <button class="cart-drawer__variant-confirm-btn" id="cart-drawer-variant-confirm">
            <span>${this.t('confirm', 'Confirm')}</span>
          </button>
        `;
        modalEl.classList.add('is-open');
      }
    } catch (err) {
      console.error('Error opening variant modal:', err);
    } finally {
      this.hideLoading();
    }
  }

  closeVariantModal() {
    const modalEl = this.querySelector('#cart-drawer-variant-modal');
    if (modalEl) {
      modalEl.classList.remove('is-open');
    }
  }

  // --- Sold Out Filtering ---
  async fetchRecommendations(productId) {
    if (this.recommendationsCache[productId]) {
      return this.recommendationsCache[productId];
    }
    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6`);
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data.products || []).filter(p => p.available !== false);
      this.recommendationsCache[productId] = products;
      return products;
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      return [];
    }
  }

  async fetchCartSection() {
    try {
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(rootPath + '?sections=cart-drawer');
      if (!res.ok) return null;
      const data = await res.json();
      return data['cart-drawer'] || null;
    } catch (err) {
      console.error('Error fetching cart-drawer section:', err);
      return null;
    }
  }

  renderCartSectionHtml(sectionHtml) {
    if (!sectionHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');

    if (!newDrawer) return;

    // Reset back button if returning from detail view
    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn && !this.isDetailView) {
      backBtn.classList.add('is-hidden');
    }

    // Swap body and footer DOM elements from server-rendered Liquid Section
    const newBody = newDrawer.querySelector('.cart-drawer__body');
    const newFooter = newDrawer.querySelector('#cart-drawer-footer') || newDrawer.querySelector('.cart-drawer__footer');

    const currentBody = this.querySelector('.cart-drawer__body');
    const currentFooter = this.querySelector('#cart-drawer-footer') || this.querySelector('.cart-drawer__footer');

    if (newBody && currentBody) {
      currentBody.innerHTML = newBody.innerHTML;
    }

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
      currentFooter.style.display = 'block';
    } else if (newFooter && !currentFooter) {
      const card = this.querySelector('.cart-drawer__card');
      if (card) {
        const footerDiv = document.createElement('div');
        footerDiv.className = 'cart-drawer__footer';
        footerDiv.id = 'cart-drawer-footer';
        footerDiv.innerHTML = newFooter.innerHTML;
        card.appendChild(footerDiv);
      }
    } else if (!newFooter && currentFooter) {
      currentFooter.style.display = 'none';
    }

    // Handle falling confetti animation on free shipping unlock
    const msgEl = this.querySelector('#free-shipping-msg');
    if (msgEl) {
      const isNowUnlocked = msgEl.dataset.unlocked === 'true';
      if (isNowUnlocked && !this.isUnlockedState) {
        this.triggerFallingConfetti();
      }
      this.isUnlockedState = isNowUnlocked;
    }
  }

  async renderCartState(cart) {
    const sectionHtml = await this.fetchCartSection();
    if (sectionHtml) {
      this.renderCartSectionHtml(sectionHtml);
    }
  }
}

// Register custom HTML5 Element
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
